import { reportUnauthorized } from '../utils/authEvents';
import { getAuthToken } from '../utils/authToken';
import { httpError } from '../utils/userError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Helper to handle 401 responses. A 401 `mfa_required` (#333) signs the user
// out at once; any other 401 takes the refresh-then-sign-out path (App.jsx).
const handleAuthError = (response) => {
    if (response.status === 401) {
        reportUnauthorized(response);
    }
};

// Build a thrown Error from a parsed JSON error body. Most blueprints return the
// nested `{error: {message}}` shape, but the contact_network blueprint (Agenda TO
// Contacts) returns FLAT `{error: "already_requested"}` discriminator strings — so
// without this fallback the UI would only ever see "API error: 409" and couldn't
// branch on the discriminator (FE-8). We also stash the status + raw body on the
// Error so callers can read e.g. `err.body.request_id` (they_requested_you) without
// re-fetching. Existing callers still read `err.message`, unchanged.
// (#439) — the shared builder now lives in utils/userError so every service
// attaches the same `status` / `body` / `ref` the display mapper reads.
const apiError = (errorBody, status) => httpError(status, errorBody);

class ApiService {
    async login(email, password) {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                // Try to extract error message from response
                let errorMessage = 'Login failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error?.message || errorData.message || errorMessage;
                } catch (e) {
                    // If response is not JSON, use status text
                    errorMessage = response.statusText || `Error ${response.status}`;
                }
                throw new Error(errorMessage);
            }

            return response.json();
        } catch (error) {
            // Handle network errors
            if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
                throw new Error('Cannot connect to server. Please check if the backend is running.');
            }
            throw error;
        }
    }

    async signup(username, email, password) {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, email, password }),
            });

            if (!response.ok) {
                // Try to extract error message from response
                let errorMessage = 'Signup failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error?.message || errorData.message || errorMessage;
                } catch (e) {
                    // If response is not JSON, use status text
                    errorMessage = response.statusText || `Error ${response.status}`;
                }
                throw new Error(errorMessage);
            }

            return response.json();
        } catch (error) {
            // Handle network errors
            if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
                throw new Error('Cannot connect to server. Please check if the backend is running.');
            }
            throw error;
        }
    }

    async checkHealth(timeoutMs = 8000) {
        // Bound the health probe so a hung/idle connection fails fast and the
        // caller can retry, instead of leaving the request (and the status
        // indicator) stuck — part of the Connection Error stability fix.
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(`${API_BASE_URL}/health`, { signal: controller.signal });
            if (!response.ok) {
                throw httpError(response.status, null);
            }
            return await response.json();
        } finally {
            clearTimeout(timer);
        }
    }

    async getConversations(page = 1, limit = 20, includeArchived = false) {
        const token = getAuthToken();
        
        if (!token) {
            throw new Error('No authentication token found');
        }
        
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            include_archived: includeArchived.toString()
        });

        const response = await fetch(`${API_BASE_URL}/conversations?${params}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        handleAuthError(response);
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Conversations API error:', response.status, response.statusText, errorText);
            throw httpError(response.status, (() => { try { return JSON.parse(errorText); } catch (_) { return null; } })());
        }

        return response.json();
    }

    async createConversation(title = null, modelName = 'llama-local', systemPrompt = null) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                title,
                model_name: modelName,
                system_prompt: systemPrompt
            })
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    async getConversation(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    // Per-conversation metrics for the Conversation Data card (user/AI message
    // counts, energy points, start date, last-used).
    async getConversationData(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/data`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    // Download a complete debug-trace export (JSON) of a conversation: every
    // turn's message + full metadata (thinking steps, prompt, sources, …) and
    // every logged Gemini call (tokens + raw input/output). Returns the blob and
    // the server-suggested filename so the caller can trigger a browser download.
    async exportConversation(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/export`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            throw new Error(`Export failed: ${response.status}`);
        }
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const filename = match ? match[1] : `pai-conversation-${conversationId}.json`;
        const blob = await response.blob();
        return { blob, filename };
    }

    async updateConversation(conversationId, updates) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    async deleteConversation(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    async bulkDeleteConversations(conversationIds) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/bulk-delete`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                conversation_ids: conversationIds
            })
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    /**
     * Global chat search. `signal` is an AbortSignal (#353): a search that has been
     * superseded by more typing must be CANCELLED, not merely ignored — an
     * un-cancelled request keeps a server thread busy scanning for a term the user
     * has already moved past, and several of those stack up behind each other.
     */
    async searchMessagesGlobal(searchQuery, { signal } = {}) {
        const token = getAuthToken();
        const params = new URLSearchParams({ q: searchQuery });
        const response = await fetch(`${API_BASE_URL}/conversations/messages/search?${params}`, {
            method: 'GET',
            signal,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    /**
     * Search inside ONE conversation.
     *
     * `preview` (#353 UI phase) asks for the SLIM per-message shape the sidebar's
     * conversation expansion renders — `{message_id, message_preview, message_type,
     * created_at, occurrences}` per matching message, complete rather than capped.
     * Omitted, the response is the full `MessageResponse` rows `DayChannelPanel`'s
     * in-day search has always consumed, byte for byte.
     *
     * `signal` cancels a superseded expansion, same discipline as the global search:
     * collapse-then-expand-another must not leave a scan running for a list nobody
     * is waiting for.
     */
    async searchMessagesInConversation(conversationId, searchQuery, { preview = false, signal } = {}) {
        const token = getAuthToken();
        const params = new URLSearchParams({ q: searchQuery });
        if (preview) params.set('preview', '1');
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/messages/search?${params}`, {
            method: 'GET',
            signal,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw httpError(response.status, null);
        }

        return response.json();
    }

    async addMessageToConversation(conversationId, content, messageType = 'user', metadata = null) {
        const token = getAuthToken();

        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/messages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                content,
                message_type: messageType,
                metadata
            })
        });

        handleAuthError(response);
        if (!response.ok) {
            const errorText = await response.text();
            throw httpError(response.status, (() => { try { return JSON.parse(errorText); } catch (_) { return null; } })());
        }

        const result = await response.json();
        return result;
    }

    async editMessage(conversationId, messageId, newContent, metadata = null) {
        const token = getAuthToken();

        // `metadata` carries a whitelisted patch (voice_segments/voice/typed_text)
        // so edit mode can add/remove/replace a message's audio components.
        const body = { content: newContent };
        if (metadata && typeof metadata === 'object') body.metadata = metadata;
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/messages/${messageId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        });

        handleAuthError(response);
        if (!response.ok) {
            const errorText = await response.text();
            throw httpError(response.status, (() => { try { return JSON.parse(errorText); } catch (_) { return null; } })());
        }

        const result = await response.json();
        return result;
    }

    // #446 Phantom turns: ask the backend to stop the agentic run answering this
    // USER message. Stop in the browser only cancels the reader; without this the
    // server-side loop ran (and billed) every remaining iteration. With `runId`
    // exactly that run is cancelled (a re-run of the same message after an edit
    // is untouched); without it only a run that started before now stops.
    async cancelRun(conversationId, messageId, runId = null) {
        const token = getAuthToken();
        const response = await fetch(
            `${API_BASE_URL}/conversations/${conversationId}/messages/${messageId}/cancel-run`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(runId ? { run_id: runId } : {}),
                // Must survive the page/tab tearing down right after Stop.
                keepalive: true,
            }
        );
        if (!response.ok) {
            throw httpError(response.status, await response.json().catch(() => null));
        }
        return response.json();
    }

    // Persist a confirmation card's final state (H-3.2). Single resolver replacing the
    // 14 copy-paste resolve* methods — hits the generic
    // PATCH /conversations/<id>/messages/<msgId>/card-resolution (H-3.1) which merges
    // {status[, count]} into the message's metadata[card_key].resolution and, when an
    // action_id is present, resolves the matching PaiAction ledger row.
    // body: { card_key, status, count?, action_id? }.
    // `omnis:card-resolved` (#507): the Main Office patches its warm buffer with
    // the card's final state. Also fired for a 409 `card_already_resolved` (#517)
    // with the decision the server KEPT, so the buffer never re-arms the card.
    _announceCardResolved(conversationId, messageId, payload, resolution) {
        try {
            window.dispatchEvent(new CustomEvent('omnis:card-resolved', {
                detail: {
                    conversationId, messageId, cardKey: payload.card_key,
                    entryId: payload.entry_id || null, resolution,
                },
            }));
        } catch (_) { /* ignore */ }
    }

    async resolveCardState(conversationId, messageId, body) {
        const token = getAuthToken();
        const payload = { card_key: body.card_key, status: body.status };
        if (body.count != null) payload.count = body.count;
        if (body.action_id) payload.action_id = body.action_id;
        // entry_id (Calendar Ui #226): resolve a specific card among many of one
        // kind on the message (e.g. one of several calendar_events_created entries,
        // or of several contact_creation_requests entries by card_id — #515).
        if (body.entry_id) payload.entry_id = body.entry_id;
        const response = await fetch(
            `${API_BASE_URL}/conversations/${conversationId}/messages/${messageId}/card-resolution`,
            {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            },
        );
        handleAuthError(response);
        if (!response.ok) {
            // #517: keep the body — a 409 `card_already_resolved` carries the
            // decision the server kept, which the card adopts.
            const errBody = await response.json().catch(() => null);
            if (response.status === 409 && errBody?.resolution?.status) {
                this._announceCardResolved(conversationId, messageId, payload, errBody.resolution);
            }
            throw httpError(response.status, errBody);
        }
        const result = await response.json();
        // #507: tell the Main Office so it patches the warm message buffer — a
        // conversation switch repaints from that buffer, which otherwise still
        // holds the PRE-confirmation payload (the card flashed "pending").
        const resolution = { status: payload.status };
        if (payload.count != null) resolution.count = payload.count;
        this._announceCardResolved(conversationId, messageId, payload, resolution);
        return result;
    }

    // Persist a batch-duplicate card's decision. This card is shape-different (a
    // free-text `resolution` string + `action`, not a {status} dict) so it keeps its
    // dedicated endpoint (batch-duplicate-resolution) rather than card-resolution.
    async resolveBatchDuplicate(conversationId, messageId, resolution, action = null) {
        const token = getAuthToken();
        const response = await fetch(
            `${API_BASE_URL}/conversations/${conversationId}/messages/${messageId}/batch-duplicate-resolution`,
            {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ resolution, action }),
            },
        );
        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    async getConversationDocuments(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/documents`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    // The "documentary footprint" of a conversation (Conversation documents #148):
    // the docs CREATED or EDITED within it (not everything merely read). Returns
    // { created: [{id,name,version}], edited: [{id,name,version}] } pointing at the
    // current latest version of each — powers the Conversation Data card section.
    async getConversationDocumentHistory(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/documents/history`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });
        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    async shareConversation(conversationId, email, sharedDocumentIds = [], canWrite = false) {
        const token = getAuthToken();
        const body = { email };
        if (sharedDocumentIds.length > 0) {
            body.shared_document_ids = sharedDocumentIds;
        }
        // Granular write permission (Agenda TO Contacts Phase D / T6). Additive —
        // omitted/false keeps the legacy read-only share behaviour.
        if (canWrite) {
            body.can_write = true;
        }
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/share`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw httpError(response.status, error);
        }
        return response.json();
    }

    async getConversationShares(conversationId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/shares`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    async unshareConversation(conversationId, userId) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/share/${userId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    async getShareContacts() {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/conversations/share-contacts`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        handleAuthError(response);
        if (!response.ok) {
            throw httpError(response.status, null);
        }
        return response.json();
    }

    // Generic HTTP methods for API calls
    async get(endpoint) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw apiError(error, response.status);
        }

        return response.json();
    }

    async post(endpoint, data) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw apiError(error, response.status);
        }

        return response.json();
    }

    async delete(endpoint) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw apiError(error, response.status);
        }

        return response.json();
    }

    async patch(endpoint, data) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw apiError(error, response.status);
        }

        return response.json();
    }

    // Permanently delete the caller's account (Agenda TO Contacts Phase D / T1
    // "Smithereens"). The BE requires the exact typed confirmation string in the
    // body, so this can't use the generic `delete()` (which sends no body).
    // Full-account data export (User Settings #184 — "Data Sovereignty"). Fetches
    // the ZIP with the auth header (a bare <a href> can't) and returns the blob +
    // server-suggested filename so the caller can trigger a browser download.
    async exportAccountData() {
        const response = await fetch(`${API_BASE_URL}/auth/account/export`, {
            headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        });
        handleAuthError(response);
        if (!response.ok) {
            throw new Error(`Data export failed: ${response.status}`);
        }
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const filename = match ? match[1] : 'pai-data-export.zip';
        const blob = await response.blob();
        return { blob, filename };
    }

    async deleteAccount(confirm = 'DELETE') {
        const response = await fetch(`${API_BASE_URL}/auth/account`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ confirm }),
        });

        handleAuthError(response);
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw apiError(error, response.status);
        }
        return response.json();
    }

    async getUserProfile() {
        const token = getAuthToken();
        console.log('getUserProfile - token from localStorage:', token ? `${token.substring(0, 20)}...` : 'NULL/EMPTY');

        const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        console.log('getUserProfile - response status:', response.status);

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            console.error('getUserProfile failed:', response.status, error);
            throw httpError(response.status, error);
        }

        return response.json();
    }
}

export default new ApiService();